import React from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import Flatpickr from 'react-flatpickr';

import { ReactComponent as NextIconSVG } from 'assets/images/location/next-icon.svg';
import { ReactComponent as PrevIconSVG } from 'assets/images/location/prev-icon.svg';

import Button from 'components/App/Button';
import SelectValidation from 'components/App/SelectValidation';
import FormField from 'components/AddLocation/FormField';
import SectionWrapper from 'components/AddLocation/SectionWrapper';

import INITIAL_VALUE from 'constants/initialValue';
import { OPTION_ABSENT_PENALTY } from 'constants/index';
import TAB from 'constants/tab';
import { PARAM_SETTING } from 'constants/setting';
import { editLocationFormSteps } from 'utils/validate';

import 'flatpickr/dist/themes/material_blue.css';
import styles from './StepPolicy.module.scss';

const optionFlatpickr = {
  enableTime: true,
  noCalendar: true,
  dateFormat: 'H:i',
  minuteIncrement: 1,
  time_24hr: true,
};

const optionAbsentPenalty = [
  OPTION_ABSENT_PENALTY.EQUAL_CANCEL_PENALTY,
  OPTION_ABSENT_PENALTY['100_PERCENT'],
];

const DEFAULT_ABSENT_PENALTY = OPTION_ABSENT_PENALTY['100_PERCENT'].value;

const getCancelBookingPolicyItem = (cancelBookingPolicy) => {
  if (Array.isArray(cancelBookingPolicy)) {
    return cancelBookingPolicy[0] || {};
  }

  return cancelBookingPolicy || {};
};

const buildCancelBookingPolicyPayload = (policy) => ([
  {
    freeBeforeDays: policy.freeBeforeDays ?? 0,
    cancelPenalty: policy.cancelPenalty,
    absentPenalty: policy.absentPenalty ?? DEFAULT_ABSENT_PENALTY,
    isApplied: policy.isApplied ?? true,
  },
]);

function StepPolicy(props) {
  const {
    setting,
    location,

    actionAddLocationByStep,
    onBack,
    setIsValid,
  } = props;

  const freeBeforeDaysSetting = setting[PARAM_SETTING.CANCEL_BOOKING_POLICY]?.find((item) => item.name === 'FREE_BEFORE_DAYS');
  const optionFreeBeforeDays = React.useMemo(() => (
    freeBeforeDaysSetting?.children
      ? freeBeforeDaysSetting.children.map((value) => {
        const numericValue = Number(value);

        if (numericValue === 0) {
          return {
            value: numericValue,
            label: 'Không hoàn hủy',
          };
        }

        return {
          value: numericValue,
          label: `Đến ${numericValue} ngày trước khi tới`,
        };
      })
      : []
  ), [freeBeforeDaysSetting]);

  const cancelPenaltySetting = setting[PARAM_SETTING.CANCEL_BOOKING_POLICY]?.find((item) => item.name === 'CANCEL_PENALTY');
  const optionCancelPenalty = React.useMemo(() => (cancelPenaltySetting
    && cancelPenaltySetting.children
    ? cancelPenaltySetting.children.map((child) => ({
      value: child.name,
      label: child.vi,
    }))
    : []), [cancelPenaltySetting]);

  const initialCancelPolicy = getCancelBookingPolicyItem(location.cancelBookingPolicy);
  const defaultCancelPenalty = optionCancelPenalty[2]?.value;

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      freeBeforeDays: Number(
        initialCancelPolicy.freeBeforeDays ?? optionFreeBeforeDays[0]?.value ?? 0,
      ),
      cancelPenalty: initialCancelPolicy.cancelPenalty ?? defaultCancelPenalty,
      absentPenalty: initialCancelPolicy.absentPenalty ?? DEFAULT_ABSENT_PENALTY,
      checkInTime: typeof location?.checkInTime === 'string'
        ? location.checkInTime
        : location?.checkInTime?.to || '14:00',
      checkOutTime: typeof location?.checkOutTime === 'string'
        ? location.checkOutTime
        : location?.checkOutTime?.to || '12:00',
      allowSmoking: location?.allowSmoking ?? false,
      allowPet: location?.allowPet ?? false,
      hasCurfew: location?.hasCurfew ?? false,
      curfewFrom: location?.curfewPolicy?.from || location?.curfewFrom || '00:00',
      curfewTo: location?.curfewPolicy?.to || location?.curfewTo || '12:00',
    },

    validationSchema: Yup.object(editLocationFormSteps.stepPolicy),

    onSubmit: (values) => {
      actionAddLocationByStep({
        location: {
          cancelBookingPolicy: buildCancelBookingPolicyPayload({
            freeBeforeDays: +values.freeBeforeDays,
            cancelPenalty: values.cancelPenalty,
            absentPenalty: values.absentPenalty,
          }),
          checkInTime: values.checkInTime,
          checkOutTime: values.checkOutTime,
          allowSmoking: values.allowSmoking,
          allowPet: values.allowPet,
          hasCurfew: values.hasCurfew,
          curfewPolicy: values.hasCurfew ? {
            from: values.curfewFrom,
            to: values.curfewTo,
          } : null,
        },
        step: TAB.PAYMENT,
        stepAvailable: TAB.POLICY,
      });
    },
  });

  const selectedFreeBeforeDaysOption = React.useMemo(
    () => optionFreeBeforeDays.find(
      (item) => item.value === validation.values.freeBeforeDays,
    ) ?? null,
    [optionFreeBeforeDays, validation.values.freeBeforeDays],
  );

  const selectedAbsentPenaltyOption = React.useMemo(
    () => optionAbsentPenalty.find(
      (item) => item.value === validation.values.absentPenalty,
    ) ?? null,
    [validation.values.absentPenalty],
  );

  const handleTimeChange = (_, currentDate, self) => {
    validation.setFieldValue(self.element.name, currentDate, true);

    const name = self.element.name.includes('checkInTime') ? 'checkInTime' : 'checkOutTime';

    const duration = self.element.name.includes('from') ? 'from' : 'to';

    actionAddLocationByStep({
      step: TAB.POLICY,
      location: {
        ...location,
        [name]: {
          ...location[name],
          [duration]: currentDate,
        },
      },
    });
  };

  const handleCurfewTimeChange = (_, currentDate, self) => {
    const { name } = self.element;
    validation.setFieldValue(name, currentDate, true);

    const nextValues = {
      ...validation.values,
      [name]: currentDate,
    };

    actionAddLocationByStep({
      step: TAB.POLICY,
      location: {
        ...location,
        curfewPolicy: {
          from: nextValues.curfewFrom,
          to: nextValues.curfewTo,
        },
      },
    });
  };

  const onChangeFreeBeforeDays = React.useCallback((e) => {
    const nextCancelPenalty = optionCancelPenalty[2]?.value;
    validation.setFieldValue('freeBeforeDays', Number(e.value), true);
    validation.setFieldValue('cancelPenalty', nextCancelPenalty, true);
    validation.setFieldValue('absentPenalty', DEFAULT_ABSENT_PENALTY, true);

    const currentPolicy = getCancelBookingPolicyItem(location.cancelBookingPolicy);

    actionAddLocationByStep({
      step: TAB.POLICY,
      location: {
        ...location,
        cancelBookingPolicy: buildCancelBookingPolicyPayload({
          ...currentPolicy,
          freeBeforeDays: +e.value,
          cancelPenalty: nextCancelPenalty ?? currentPolicy.cancelPenalty,
          absentPenalty: DEFAULT_ABSENT_PENALTY,
        }),
      },
    });
  }, [actionAddLocationByStep, location, optionCancelPenalty, validation]);

  const onChangeCancelPenalty = React.useCallback((e) => {
    validation.setFieldValue('cancelPenalty', e.value, false);

    const currentPolicy = getCancelBookingPolicyItem(location.cancelBookingPolicy);

    actionAddLocationByStep({
      step: TAB.POLICY,
      location: {
        ...location,
        cancelBookingPolicy: buildCancelBookingPolicyPayload({
          ...currentPolicy,
          cancelPenalty: e.value,
        }),
      },
    });
  }, [actionAddLocationByStep, location, validation]);

  const onChangeAbsentPenalty = React.useCallback((e) => {
    validation.setFieldValue('absentPenalty', e.value, false);

    const currentPolicy = getCancelBookingPolicyItem(location.cancelBookingPolicy);

    actionAddLocationByStep({
      step: TAB.POLICY,
      location: {
        ...location,
        cancelBookingPolicy: buildCancelBookingPolicyPayload({
          ...currentPolicy,
          absentPenalty: e.value,
        }),
      },
    });
  }, [actionAddLocationByStep, location, validation]);

  const handleRadioChange = (e) => {
    const value = !!+e.target.value;
    validation.setFieldValue(e.target.name, value);

    const curfewPolicyUpdate = e.target.name === 'hasCurfew' ? {
      curfewPolicy: value ? {
        from: validation.values.curfewFrom || '00:00',
        to: validation.values.curfewTo || '12:00',
      } : null,
    } : {};

    actionAddLocationByStep({
      step: TAB.POLICY,
      location: {
        ...location,
        [e.target.name]: value,
        ...curfewPolicyUpdate,
      },
    });
  };

  React.useEffect(() => {
    setIsValid(validation.isValid);
  }, [location, setIsValid, validation.isValid]);

  return (
    <form className={styles.Component} onSubmit={validation.handleSubmit}>
      <SectionWrapper title="Hủy đặt phòng" isRequired>
        <FormField label="Khách có thể hủy đặt phòng miễn phí bao lâu trước khi tới nơi?">
          <SelectValidation
            placeholder="Đến 3 ngày trước khi tới nơi"
            name="freeBeforeDays"
            options={optionFreeBeforeDays}
            value={selectedFreeBeforeDaysOption}
            isUsePureValue
            validation={validation}
            onChange={onChangeFreeBeforeDays}
          />
        </FormField>

        <FormField label={`Khách phải trả bao nhiêu khi hủy đặt phòng ${validation.values.freeBeforeDays === 0 ? '' : `trong vòng ${validation.values.freeBeforeDays} ngày `}trước khi tới nhận phòng?`}>
          <SelectValidation
            placeholder="100% tiền phòng"
            name="cancelPenalty"
            options={optionCancelPenalty}
            value={validation.values.cancelPenalty}
            validation={validation}
            isDisabled={validation.values.freeBeforeDays === 0}
            onChange={onChangeCancelPenalty}
          />
        </FormField>

        <FormField label="Bạn sẽ thu phí bao nhiêu nếu khách vắng mặt?">
          <SelectValidation
            placeholder="100% tổng tiền phòng"
            name="absentPenalty"
            options={optionAbsentPenalty}
            value={selectedAbsentPenaltyOption}
            isUsePureValue
            validation={validation}
            isDisabled={validation.values.freeBeforeDays === 0}
            onChange={onChangeAbsentPenalty}
          />
        </FormField>
      </SectionWrapper>

      <SectionWrapper title="Giờ nhận phòng" isRequired>
        <Flatpickr
          name="checkInTime"
          className="Section__Content__TimePicker"
          defaultValue={validation.values.checkInTime}
          options={optionFlatpickr}
          onChange={handleTimeChange}
        />
      </SectionWrapper>

      <SectionWrapper title="Giờ trả phòng" isRequired>
        <Flatpickr
          name="checkOutTime"
          className="Section__Content__TimePicker"
          defaultValue={validation.values.checkOutTime}
          options={optionFlatpickr}
          onChange={handleTimeChange}
        />
      </SectionWrapper>

      <SectionWrapper title="Giờ giới nghiêm" isRequired>
        <div className="BtnRadioWrapper">
          <div className="Box">
            <input
              className="InputRadio"
              type="radio"
              id="curfew-yes"
              name="hasCurfew"
              value="1"
              onChange={handleRadioChange}
              checked={validation.values.hasCurfew === true}
            />
            <label htmlFor="curfew-yes">Có</label>
          </div>
          <div className="Box">
            <input
              className="InputRadio"
              type="radio"
              id="curfew-no"
              name="hasCurfew"
              value="0"
              onChange={handleRadioChange}
              checked={validation.values.hasCurfew === false}
            />
            <label htmlFor="curfew-no">Không</label>
          </div>
        </div>

        {validation.values.hasCurfew && (
          <div className="row g-4">
            <div className="col-6">
              <FormField label="Sau">
                <Flatpickr
                  name="curfewFrom"
                  className="Section__Content__TimePicker"
                  value={validation.values.curfewFrom}
                  options={optionFlatpickr}
                  onChange={handleCurfewTimeChange}
                />
              </FormField>
            </div>

            <div className="col-6">
              <FormField label="Đến">
                <Flatpickr
                  name="curfewTo"
                  className="Section__Content__TimePicker"
                  value={validation.values.curfewTo}
                  options={optionFlatpickr}
                  onChange={handleCurfewTimeChange}
                />
              </FormField>
            </div>
          </div>
        )}
      </SectionWrapper>

      <SectionWrapper title="Hút thuốc và Thú cưng" isRequired>
        <div className="BtnRadioWrapper">
          <div className="Title">Hút thuốc</div>

          <div className="Box">
            <input
              className="InputRadio"
              type="radio"
              id="smoke-allow"
              name="allowSmoking"
              value="1"
              onChange={handleRadioChange}
              defaultChecked={validation.values.allowSmoking}
            />
            <label htmlFor="smoke-allow">Cho phép</label>
          </div>
          <div className="Box">
            <input
              className="InputRadio"
              type="radio"
              id="smoke-deny"
              name="allowSmoking"
              value="0"
              onChange={handleRadioChange}
              defaultChecked={!validation.values.allowSmoking}
            />
            <label htmlFor="smoke-deny">Không cho phép</label>
          </div>
        </div>

        <div className="BtnRadioWrapper">
          <div className="Title">Thú cưng</div>

          <div className="Box">
            <input
              className="InputRadio"
              type="radio"
              id="pets-allow"
              name="allowPet"
              value="1"
              onChange={handleRadioChange}
              defaultChecked={validation.values.allowPet}
            />
            <label htmlFor="pets-allow">Cho phép</label>
          </div>
          <div className="Box">
            <input
              className="InputRadio"
              type="radio"
              id="pets-deny"
              name="allowPet"
              value="0"
              onChange={handleRadioChange}
              defaultChecked={!validation.values.allowPet}
            />
            <label htmlFor="pets-deny">Không cho phép</label>
          </div>
        </div>
      </SectionWrapper>

      <div className={styles.FormAction}>
        <Button
          classes="Btn Btn__PrevStep"
          outline
          onClick={onBack(TAB.ADD_IMAGES)}
        >
          <PrevIconSVG className="Icon__PrevStep" />
          Quay về bước trước
        </Button>

        <Button
          classes="Btn Btn__NextStep"
          type="submit"
        >
          Bước tiếp theo
          <NextIconSVG className="Icon__NextStep" />
        </Button>
      </div>
    </form>
  );
}

StepPolicy.propTypes = {
  setting: PropTypes.instanceOf(Object),
  location: PropTypes.instanceOf(Object).isRequired,

  actionAddLocationByStep: PropTypes.func,
  onBack: PropTypes.func,
  setIsValid: PropTypes.func,
};

StepPolicy.defaultProps = {
  setting: INITIAL_VALUE.ARRAY,

  actionAddLocationByStep: INITIAL_VALUE.FUNCTION,
  onBack: INITIAL_VALUE.FUNCTION,
  setIsValid: INITIAL_VALUE.FUNCTION,
};

export default React.memo(StepPolicy);
